
import React from 'react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCw, FileWarningIcon, FileCheckIcon, ExternalLinkIcon, InfoIcon } from "lucide-react";
import { ImageUpload } from './ImageUpload';
import { useOgImage } from '@/hooks/useOgImage';

export const OgImageSection = () => {
  const {
    ogImageExists,
    isCheckingImage,
    lastChecked,
    displayImageUrl,
    manualCheckImage
  } = useOgImage();

  return (
    <div className="bg-green-600/20 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-green-300/30 mt-8">
      <h2 className="text-2xl font-bold text-white mb-4">Social Media Vorschaubild</h2>
      <p className="text-white mb-4">
        Hier können Sie das Bild hochladen, das angezeigt wird, wenn jemand Ihre Seite auf Facebook, Twitter oder anderen sozialen Medien teilt.
      </p>
      
      <Alert className="mb-4 bg-amber-100 text-amber-900">
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>Facebook Cache-Hinweis</AlertTitle>
        <AlertDescription>
          Nach dem Hochladen eines neuen Vorschaubildes kann es bis zu 24 Stunden dauern, bis Facebook den Cache aktualisiert und das neue Bild anzeigt. 
          Sie können den Cache manuell aktualisieren mit dem <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" className="underline font-bold">Facebook Debug Tool</a>.
        </AlertDescription>
      </Alert>
      
      <Alert className={`mb-4 ${
        isCheckingImage 
          ? 'bg-blue-100 text-blue-900' 
          : ogImageExists === true 
            ? 'bg-green-100 text-green-900' 
            : 'bg-red-100 text-red-900'
      }`}>
        {isCheckingImage ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : ogImageExists === true ? (
          <FileCheckIcon className="h-4 w-4" />
        ) : (
          <FileWarningIcon className="h-4 w-4" />
        )}
        
        <AlertTitle>OG Image URL Status</AlertTitle>
        <AlertDescription>
          Das Social Media Vorschaubild wird unter folgender URL gespeichert: 
          <code className="block mt-2 p-2 bg-gray-100 rounded text-sm break-all">
            {displayImageUrl}
          </code>
          
          {isCheckingImage ? (
            <div className="mt-2">Überprüfe Bildverfügbarkeit...</div>
          ) : ogImageExists === true ? (
            <div className="mt-2 flex items-center text-green-700">
              <span className="mr-2">✓</span> Das Bild ist unter dieser URL verfügbar.
            </div>
          ) : (
            <div className="mt-2 flex items-center text-red-700">
              <span className="mr-2">✗</span> Das Bild ist unter dieser URL NICHT verfügbar. Bitte laden Sie ein neues Bild hoch.
            </div>
          )}
          
          {lastChecked && (
            <div className="mt-1 text-xs text-gray-500">
              Zuletzt geprüft: {lastChecked.toLocaleTimeString()}
            </div>
          )}
          
          <div className="mt-4 flex gap-2">
            <a 
              href={displayImageUrl}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center text-blue-700 hover:underline"
            >
              Bild direkt öffnen <ExternalLinkIcon className="ml-1 h-4 w-4" />
            </a>
            
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={manualCheckImage}
              disabled={isCheckingImage}
            >
              {isCheckingImage ? 'Prüfe...' : 'Überprüfen'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
      
      <ImageUpload defaultSection="meta-image" defaultSubsection="og-image" />
    </div>
  );
};
